import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
  redirectTo?: string
}

interface User {
  id: number
  email: string
  role: string
  permissions: string[]
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [],
  redirectTo = '/unauthorized'
}) => {
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthorized, setIsAuthorized] = useState(false)
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    checkAuthorization()
  }, [])

  const checkAuthorization = async () => {
    try {
      // Check if JWT token exists
      const token = localStorage.getItem('stratiqx_access_token')
      
      if (!token) {
        console.warn('🔒 ProtectedRoute: No authentication token found')
        setIsAuthorized(false)
        setIsChecking(false)
        return
      }

      // Decode JWT to get user info (in production, validate with backend)
      const payload = JSON.parse(atob(token.split('.')[1]))
      const userData: User = {
        id: payload.id,
        email: payload.email,
        role: payload.role,
        permissions: payload.permissions || []
      }
      
      setUser(userData)

      // Check if user has required role
      if (requiredRoles.length === 0) {
        // No specific role required, just needs to be authenticated
        setIsAuthorized(true)
      } else {
        // Check if user's role is in the required roles list
        const hasRequiredRole = requiredRoles.includes(userData.role)
        
        if (!hasRequiredRole) {
          console.warn(`🚫 ProtectedRoute: User role '${userData.role}' not in required roles:`, requiredRoles)
        }
        
        setIsAuthorized(hasRequiredRole)
      }
    } catch (error) {
      console.error('❌ ProtectedRoute: Error checking authorization:', error)
      setIsAuthorized(false)
    } finally {
      setIsChecking(false)
    }
  }

  // Still checking authorization
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Checking authorization...</span>
      </div>
    )
  }

  // Not authorized - redirect
  if (!isAuthorized) {
    console.log('🔀 ProtectedRoute: Redirecting unauthorized user to:', redirectTo)
    
    // If no token at all, redirect to login
    if (!localStorage.getItem('stratiqx_access_token')) {
      return <Navigate to="/" replace />
    }
    
    // If has token but wrong role, show unauthorized message
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this page.
            </p>
            {user && (
              <div className="bg-gray-50 rounded p-3 mb-4">
                <p className="text-sm text-gray-500">Your role: <span className="font-medium text-gray-700">{user.role}</span></p>
                {requiredRoles.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    Required: <span className="font-medium text-gray-700">{requiredRoles.join(' or ')}</span>
                  </p>
                )}
              </div>
            )}
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Authorized - render children
  return <>{children}</>
}